import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useAdvertiser } from '../contexts/AdvertiserContext';
import ChooseClientPlaceholder from './ChooseClientPlaceholder';
import { useTranslation, Trans } from 'react-i18next';
import { MapContainer, TileLayer, CircleMarker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import '../styles/url-lists.css';

interface GeoList {
  id: string;
  display_name: string;
  location_count: number;
  targeted_line_item_count?: number;
  is_locked?: boolean;
}

interface Location {
  lat: number;
  lng: number;
  address: string;
}

// Component to update map bounds when locations change
const MapUpdater = ({ locations, listId }: { locations: Location[], listId?: string }) => {
  const map = useMap();

  useEffect(() => {
    if (locations.length === 0) return;

    // Small delay to ensure map is ready
    const timeoutId = setTimeout(() => {
      // Check if map container is still valid (not destroyed)
      try {
        const container = map.getContainer();
        if (!container || !document.body.contains(container)) return;

        if (locations.length === 1) {
          map.setView([locations[0].lat, locations[0].lng], 12);
        } else {
          const lats = locations.map(l => l.lat);
          const lngs = locations.map(l => l.lng);
          const bounds: [[number, number], [number, number]] = [
            [Math.min(...lats), Math.min(...lngs)],
            [Math.max(...lats), Math.max(...lngs)]
          ];
          map.fitBounds(bounds, { padding: [30, 30] });
        }
        map.invalidateSize();
      } catch (e) {
        // Map was destroyed, ignore
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [locations, listId, map]);

  return null;
};

const SuperGeoListsPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAnalyst = user?.role === 'analyst' || user?.role === 'agency_analyst';
  const { selectedAdvertiser, isAllClients } = useAdvertiser();
  const selectedAdvertiserId = selectedAdvertiser?.advertiser_id || '';
  // State
  const [geoLists, setGeoLists] = useState<GeoList[]>([]);
  const [selectedList, setSelectedList] = useState<GeoList | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form state
  const [listName, setListName] = useState('');
  const [locationsText, setLocationsText] = useState('');
  const [originalLocations, setOriginalLocations] = useState<Location[]>([]);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load geo lists when advertiser changes
  useEffect(() => {
    if (selectedAdvertiserId) {
      loadGeoLists();
      setSelectedList(null);
      setIsCreating(false);
    }
  }, [selectedAdvertiserId]);

  const loadGeoLists = async () => {
    if (!selectedAdvertiserId) return;
    setLoading(true);
    try {
      const response = await api.get(`/api/proximity-lists`, {
        params: { advertiser_id: selectedAdvertiserId }
      });
      setGeoLists(response.data.lists || []);
    } catch (err) {
      console.error('Error loading geo lists:', err);
      setGeoLists([]);
    } finally {
      setLoading(false);
    }
  };

  const loadListLocations = async (listId: string) => {
    try {
      const response = await api.get(`/api/proximity-lists/${listId}/locations`, {
        params: { advertiser_id: selectedAdvertiserId }
      });
      const locations = response.data.locations || [];
      setOriginalLocations(locations);
      // Format: lat,lng,address per line
      setLocationsText(locations.map((loc: Location) =>
        `${loc.lat},${loc.lng}${loc.address ? ',' + loc.address : ''}`
      ).join('\n'));
    } catch (err) {
      console.error('Error loading locations:', err);
      setLocationsText('');
      setOriginalLocations([]);
    }
  };

  const parseLocations = (text: string): Location[] => {
    const lines = text.split('\n').map(s => s.trim()).filter(s => s.length > 0);
    const locations: Location[] = [];

    for (const line of lines) {
      const parts = line.split(',').map(p => p.trim());
      if (parts.length >= 2) {
        const lat = parseFloat(parts[0]);
        const lng = parseFloat(parts[1]);
        if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          locations.push({
            lat,
            lng,
            address: parts.slice(2).join(',').trim() || ''
          });
        }
      }
    }

    return locations;
  };

  const handleSelectList = async (list: GeoList) => {
    // Clear locations first to force map re-render with new data
    setLocationsText('');
    setOriginalLocations([]);

    setSelectedList(list);
    setIsCreating(false);
    setListName(list.display_name);
    setError('');
    setSuccess('');
    await loadListLocations(list.id);
  };

  const handleCreateNew = () => {
    setSelectedList(null);
    setIsCreating(true);
    setListName('');
    setLocationsText('');
    setOriginalLocations([]);
    setError('');
    setSuccess('');
    setTimeout(() => textareaRef.current?.focus(), 100);
  };

  const handleSave = async () => {
    if (!listName.trim()) {
      setError(t('lists.errNameRequired'));
      return;
    }

    const locations = parseLocations(locationsText);

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (isCreating) {
        // Create new list
        const response = await api.post(`/api/proximity-lists/create`, {
          advertiser_id: selectedAdvertiserId,
          display_name: listName.trim(),
          locations: locations
        });

        setSuccess(t('lists.okCreatedGeo', { name: listName, count: response.data.locations_count }));
        await loadGeoLists();

        // Select the newly created list
        const newList = {
          id: response.data.id,
          display_name: listName.trim(),
          location_count: response.data.locations_count
        };
        setSelectedList(newList);
        setIsCreating(false);
        setOriginalLocations(locations);
      } else if (selectedList) {
        // Update existing list
        // Check if name changed
        if (listName.trim() !== selectedList.display_name) {
          await api.patch(`/api/proximity-lists/${selectedList.id}`,
            { display_name: listName.trim() },
            { params: { advertiser_id: selectedAdvertiserId } }
          );
        }

        // Update locations
        const response = await api.put(
          `/api/proximity-lists/${selectedList.id}/locations`,
          { locations: locations },
          { params: { advertiser_id: selectedAdvertiserId } }
        );

        setOriginalLocations(locations);
        setSuccess(response.data.message || t('lists.okUpdated'));
        await loadGeoLists();

        // Update selected list
        setSelectedList({
          ...selectedList,
          display_name: listName.trim(),
          location_count: locations.length
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || t('lists.errSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    if (!selectedList) return;
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedList) return;

    setIsDeleting(true);
    try {
      await api.delete(`/api/proximity-lists/${selectedList.id}`, {
        params: { advertiser_id: selectedAdvertiserId }
      });
      setShowDeleteModal(false);
      setSuccess(t('lists.okDeleted'));
      setSelectedList(null);
      setIsCreating(false);
      await loadGeoLists();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('lists.errDeleting'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setSelectedList(null);
    setIsCreating(false);
    setListName('');
    setLocationsText('');
    setOriginalLocations([]);
    setError('');
    setSuccess('');
  };

  // Esc key handler for modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showDeleteModal && !isDeleting) {
        setShowDeleteModal(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showDeleteModal, isDeleting]);

  // Filter lists by search
  const filteredLists = geoLists.filter(list =>
    list.display_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Parse current locations for display
  const currentLocations = parseLocations(locationsText);

  if (isAllClients) return <ChooseClientPlaceholder pageKey="nav.superGeoLists" />;

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('lists.geoTitle')}</h1>
          <p className="header-subtitle">{t('lists.geoSubtitle')}</p>
        </div>
      </div>

      {/* Main Layout */}
      <div className="url-lists-layout">
        {/* Left Panel - Lists */}
        <aside className="url-lists-panel">
          {/* Search */}
          <div className="panel-section">
            <div className="search-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="search-input"
                placeholder={t('lists.searchLists')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Add Button */}
          {!isAnalyst && (
          <button className="add-list-btn" onClick={handleCreateNew}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>{t('lists.addList')}</span>
          </button>
          )}

          {/* Lists */}
          <div className="lists-container">
            {loading ? (
              <div className="lists-loading">
                <div className="spinner"></div>
                <span>{t('lists.loadingLists')}</span>
              </div>
            ) : filteredLists.length === 0 ? (
              <div className="lists-empty">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{t(searchQuery ? 'lists.noMatching' : 'lists.noGeoLists')}</span>
              </div>
            ) : (
              filteredLists.map(list => (
                <div
                  key={list.id}
                  className={`list-item ${selectedList?.id === list.id ? 'active' : ''}`}
                  onClick={() => handleSelectList(list)}
                >
                  <div className="list-item-icon geo-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="10" r="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="list-item-content">
                    <div className="list-item-name" style={list.is_locked ? { color: '#888' } : undefined}>
                      {list.is_locked && (
                        <span title={t('lists.legacyReadOnly')} style={{ marginRight: 6, color: '#888' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: 'middle' }}>
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/>
                          </svg>
                        </span>
                      )}
                      {list.display_name}
                    </div>
                    <div className="list-item-meta">
                      {t('lists.geoCount', { count: list.location_count || 0 })}
                    </div>
                  </div>
                  <svg className="list-item-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </div>
              ))
            )}
          </div>
        </aside>

        {/* Right Panel - Editor */}
        <div className="url-editor-panel">
          {!selectedList && !isCreating ? (
            <div className="editor-empty">
              <div className="editor-empty-icon geo-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="10" r="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{t('lists.selectGeoList')}</h3>
              <p>{t('lists.chooseFromPanel')}</p>
              {!isAnalyst && (
              <button className="editor-empty-btn" onClick={handleCreateNew}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                {t('lists.createNewList')}
              </button>
              )}
            </div>
          ) : (
            <div className="editor-content geo-editor">
              {/* Editor Header */}
              <div className="editor-header">
                <h2>{t(isCreating ? 'lists.newGeoList' : 'lists.editGeoList')}</h2>
              </div>

              {/* Alerts */}
              {error && (
                <div className="alert alert-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{error}</span>
                  <button onClick={() => setError('')}>&times;</button>
                </div>
              )}
              {success && (
                <div className="alert alert-success">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"></path>
                    <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"></polyline>
                  </svg>
                  <span>{success}</span>
                  <button onClick={() => setSuccess('')}>&times;</button>
                </div>
              )}

              {/* Two-column layout: Form + Map */}
              <div className="geo-editor-grid">
                {/* Left: Form inputs */}
                <div className="geo-editor-form">
                  <div className="form-group">
                    <label className="form-label">{t('lists.listName')}</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder={t('lists.listNamePh')}
                      value={listName}
                      onChange={(e) => setListName(e.target.value)}
                    />
                  </div>

                  <div className="form-group form-group-grow">
                    <label className="form-label">
                      {t('lists.locationsLabel')}
                      <span className="form-label-hint">{t('lists.latLngPerLine')}</span>
                    </label>
                    <textarea
                      ref={textareaRef}
                      className="form-textarea"
                      placeholder="53.3498,-6.2603&#10;53.3382,-6.2591&#10;53.3419,-6.2617"
                      value={locationsText}
                      onChange={(e) => setLocationsText(e.target.value)}
                    />
                  </div>
                </div>

                {/* Right: Map */}
                <div className="geo-editor-map">
                  {currentLocations.length === 0 ? (
                    <div className="proximity-map-empty">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                        <circle cx="12" cy="9" r="2.5"/>
                      </svg>
                      <span>{t('lists.addLocationsHint')}</span>
                    </div>
                  ) : (
                    <MapContainer
                      key={selectedList?.id || 'new'}
                      center={[currentLocations[0].lat, currentLocations[0].lng]}
                      zoom={10}
                      className="proximity-map-container"
                      scrollWheelZoom={true}
                    >
                      {/* Esri "Light Gray Canvas": a clean light-grey basemap (like the old
                          CARTO Positron), keyless. Base first, then a labels overlay on top. */}
                      <TileLayer
                        attribution='Tiles &copy; <a href="https://www.esri.com/">Esri</a>'
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                      />
                      <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
                      />
                      <MapUpdater locations={originalLocations.length > 0 ? originalLocations : currentLocations} listId={selectedList?.id} />
                      {currentLocations.map((loc, idx) => (
                        <CircleMarker
                          key={idx}
                          center={[loc.lat, loc.lng]}
                          radius={8}
                          pathOptions={{
                            color: '#fff',
                            fillColor: '#FF6B4A',
                            fillOpacity: 1,
                            weight: 3
                          }}
                        />
                      ))}
                    </MapContainer>
                  )}
                </div>
              </div>

              {/* Stats - outside grid */}
              <div className="editor-stats">
                <div className="stat-item">
                  <span className="stat-value">{currentLocations.length}</span>
                  <span className="stat-label">{t('lists.locationsLabel')}</span>
                </div>
                {!isCreating && selectedList && (
                  <div className="stat-item">
                    <span className="stat-value">{originalLocations.length}</span>
                    <span className="stat-label">{t('lists.locationsSaved')}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="editor-actions">
                <div className="editor-actions-left">
                  {!isAnalyst && !isCreating && selectedList && !selectedList.is_locked && (() => {
                    const targetedCount = selectedList.targeted_line_item_count || 0;
                    const isUsed = targetedCount > 0;
                    return (
                      <div
                        className={`tooltip-wrapper ${isUsed ? 'has-tooltip' : ''}`}
                        data-tooltip={isUsed ? t('lists.usedInGroups', { count: targetedCount }) : undefined}
                      >
                        <button
                          className="btn-danger"
                          onClick={handleDeleteClick}
                          disabled={saving || isUsed}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                          {t('common.delete')}
                        </button>
                      </div>
                    );
                  })()}
                </div>
                {!isAnalyst && !selectedList?.is_locked && (
                <div className="editor-actions-right">
                  <button
                    className="btn-secondary"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleSave}
                    disabled={saving || !listName.trim()}
                  >
                    {saving ? (
                      <>
                        <div className="btn-spinner"></div>
                        {t('lists.saving')}
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                          <polyline points="17 21 17 13 7 13 7 21"></polyline>
                          <polyline points="7 3 7 8 15 8"></polyline>
                        </svg>
                        {t(isCreating ? 'lists.createList' : 'lists.saveChanges')}
                      </>
                    )}
                  </button>
                </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedList && (
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteModal(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, visibility: 'visible' }}>
          <div className="message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="message-modal-icon delete">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </div>
            <h3>{t('lists.deleteListQ')}</h3>
            <p>
              <Trans
                i18nKey="lists.aboutToDelete"
                values={{ name: selectedList.display_name }}
                components={{ b: <strong /> }}
              /><br/>
              {t('lists.cannotUndo')}
            </p>
            <div className="message-modal-actions">
              <button
                className="message-modal-btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                {t('common.cancel')}
              </button>
              <button
                className="message-modal-btn-danger"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t('lists.deleting')}
                  </>
                ) : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SuperGeoListsPage;
